import { message, type UploadFile } from 'antd'
import { postJSON } from 'common/services/api'
import { useMutation, useQueryClient } from 'react-query'
import { uploadDocuments } from '../../../../../common/utils/documentUpload'
import { SERVICE_RECORDS_CATEGORY_ID } from '../constants'
import type { CreateServiceRecordInput, ServiceRecord } from '../types'

interface UseServiceRecordCreateReturn {
  createServiceRecord: (
    input: CreateServiceRecordInput,
    residentId: string,
    fileList?: UploadFile[]
  ) => Promise<ServiceRecord>
  isCreating: boolean
}

export const useServiceRecordCreate = (
  onSuccess?: () => void
): UseServiceRecordCreateReturn => {
  const queryClient = useQueryClient()

  const mutation = useMutation(
    async ({
      input,
      residentId,
      fileList
    }: {
      input: CreateServiceRecordInput
      residentId: string
      fileList?: UploadFile[]
    }) => {
      const hasFiles = fileList && fileList.length > 0
      let documentIds: string[] = []

      if (hasFiles) {
        try {
          documentIds = await uploadDocuments(
            fileList,
            residentId,
            SERVICE_RECORDS_CATEGORY_ID
          )
        } catch {
          throw new Error('Failed to upload documents')
        }
      }

      const response = await postJSON({
        url: `/v6/residents/${residentId}/service-records`,
        json: {
          ...input,
          ...(documentIds.length > 0 && { documents: documentIds })
        }
      })

      return response.data as ServiceRecord
    },
    {
      onSuccess: (_, variables) => {
        message.success('Service record created successfully')
        queryClient.invalidateQueries(['service-records', variables.residentId])
        if (onSuccess) {
          onSuccess()
        }
      },
      onError: (error: any) => {
        message.error(error?.message || 'Failed to create service record')
      }
    }
  )

  const createServiceRecord = async (
    input: CreateServiceRecordInput,
    residentId: string,
    fileList?: UploadFile[]
  ): Promise<ServiceRecord> => {
    return mutation.mutateAsync({ input, residentId, fileList })
  }

  return {
    createServiceRecord,
    isCreating: mutation.isLoading
  }
}
